import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Cookie, Settings, Eye, Shield, Globe } from 'lucide-react';

interface CookiePolicyProps {
  onBack?: () => void;
}

export function CookiePolicy({ onBack }: CookiePolicyProps) {
  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 border-b border-orange-500/20">
        <div className="container mx-auto px-6 py-8">
          <div className="flex items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10 font-cyberpunk"
              onClick={onBack || (() => window.history.back())}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Site
            </Button>
          </div>
          
          <div className="text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Cookie className="w-8 h-8 text-orange-400" />
              <h1 className="text-4xl font-cyberpunk text-transparent bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text">
                Cookie Policy
              </h1>
            </div>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Learn about how we use cookies and similar technologies to enhance your gaming experience on our platform.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="max-w-4xl mx-auto">
          
          {/* Last Updated */}
          <Card className="bg-slate-800/50 border-orange-500/20 mb-8">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 text-orange-400">
                <Cookie className="w-5 h-5" />
                <span className="font-cyberpunk">Last Updated: January 11, 2025</span>
              </div>
            </CardContent>
          </Card>

          {/* Content Sections */}
          <div className="space-y-8">
            
            {/* What Are Cookies */}
            <Card className="bg-slate-800/30 border-orange-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-orange-400 font-cyberpunk">
                  <Cookie className="w-6 h-6" />
                  What Are Cookies?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-700/30 rounded-lg border border-orange-500/10">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    Cookies are small text files that are placed on your device when you visit our website. They help us provide you with a better gaming experience by remembering your preferences and analyzing how you use our platform.
                  </p>
                  <p className="text-gray-300 leading-relaxed">
                    These digital markers enable us to enhance your cosmic gaming journey by personalizing content, optimizing performance, and providing seamless navigation across our gaming universe.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Types of Cookies */}
            <Card className="bg-slate-800/30 border-amber-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-amber-400 font-cyberpunk">
                  <Settings className="w-6 h-6" />
                  Types of Cookies We Use
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="p-4 bg-slate-700/30 rounded-lg border border-amber-500/10">
                    <h4 className="text-orange-400 font-cyberpunk mb-2">Essential Cookies</h4>
                    <p className="text-gray-300 leading-relaxed">
                      These cookies are necessary for our website to function properly. They enable basic features like security, network management, and accessibility.
                    </p>
                  </div>
                  <div className="p-4 bg-slate-700/30 rounded-lg border border-amber-500/10">
                    <h4 className="text-orange-400 font-cyberpunk mb-2">Performance Cookies</h4>
                    <p className="text-gray-300 leading-relaxed">
                      These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.
                    </p>
                  </div>
                  <div className="p-4 bg-slate-700/30 rounded-lg border border-amber-500/10">
                    <h4 className="text-orange-400 font-cyberpunk mb-2">Functionality Cookies</h4>
                    <p className="text-gray-300 leading-relaxed">
                      These cookies enable enhanced functionality and personalization, such as remembering your login details and game preferences.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* How We Use Cookies */}
            <Card className="bg-slate-800/30 border-orange-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-orange-400 font-cyberpunk">
                  <Eye className="w-6 h-6" />
                  How We Use Cookies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-700/30 rounded-lg border border-orange-500/10">
                  <ul className="text-gray-300 leading-relaxed space-y-2">
                    <li>• <strong className="text-orange-400">Authentication:</strong> Keep you logged in to your Cosmic Forces account</li>
                    <li>• <strong className="text-orange-400">Preferences:</strong> Remember your gaming settings and display preferences</li>
                    <li>• <strong className="text-orange-400">Analytics:</strong> Understand how our games and website are used</li>
                    <li>• <strong className="text-orange-400">Security:</strong> Protect against fraudulent activity and enhance security</li>
                    <li>• <strong className="text-orange-400">Performance:</strong> Optimize loading times and improve site functionality</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Managing Cookies */}
            <Card className="bg-slate-800/30 border-amber-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-amber-400 font-cyberpunk">
                  <Settings className="w-6 h-6" />
                  Managing Your Cookie Preferences
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-700/30 rounded-lg border border-amber-500/10">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    You have control over cookies and can manage your preferences through your browser settings. Most browsers allow you to:
                  </p>
                  <ul className="text-gray-300 leading-relaxed space-y-2">
                    <li>• View what cookies are stored on your device</li>
                    <li>• Delete cookies from your device</li>
                    <li>• Block cookies from being saved</li>
                    <li>• Configure notifications when cookies are sent</li>
                  </ul>
                  <p className="text-gray-300 leading-relaxed mt-4">
                    <strong className="text-orange-400">Note:</strong> Disabling certain cookies may affect the functionality of our games and website features.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Third-Party Cookies */}
            <Card className="bg-slate-800/30 border-orange-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-orange-400 font-cyberpunk">
                  <Globe className="w-6 h-6" />
                  Third-Party Cookies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-700/30 rounded-lg border border-orange-500/10">
                  <p className="text-gray-300 leading-relaxed mb-4">
                    We may also use third-party cookies from trusted partners to enhance your gaming experience:
                  </p>
                  <ul className="text-gray-300 leading-relaxed space-y-2">
                    <li>• <strong className="text-orange-400">Analytics Services:</strong> To understand user behavior and improve our games</li>
                    <li>• <strong className="text-orange-400">Social Media:</strong> To enable social sharing features</li>
                    <li>• <strong className="text-orange-400">Advertising:</strong> To deliver relevant gaming content and promotions</li>
                    <li>• <strong className="text-orange-400">Support Tools:</strong> To provide customer service and technical support</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-slate-800/30 border-orange-500/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-3 text-orange-400 font-cyberpunk">
                  <Shield className="w-6 h-6" />
                  Questions About Cookies?
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-slate-700/30 rounded-lg border border-orange-500/10">
                  <p className="text-gray-300 leading-relaxed">
                    If you have any questions about our use of cookies or this Cookie Policy, please contact us through our support channels. We're here to help you navigate the digital cosmos with confidence.
                  </p>
                </div>
              </CardContent>
            </Card>

          </div>

          {/* Additional Information */}
          <Card className="bg-slate-800/20 border-amber-500/20 mt-8 mb-8">
            <CardContent className="p-6">
              <div className="text-center space-y-4">
                <p className="text-gray-300 leading-relaxed">
                  This Cookie Policy is part of our commitment to transparency and your privacy rights in the digital gaming universe.
                </p>
                <div className="flex items-center justify-center gap-2 text-orange-400 font-cyberpunk text-sm">
                  <Cookie className="w-4 h-4" />
                  <span>Your preferences power your cosmic gaming experience</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Footer Navigation */}
          <div className="text-center">
            <Button 
              className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white px-8 py-3 rounded-full font-cyberpunk transition-all duration-300 transform hover:scale-105"
              onClick={onBack || (() => window.history.back())}
            >
              Return to Site
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}